<template>
  <transition name="tile" mode="out-in">
    <div
      :key="tile.broadcastId"
      ref="tile"
      class="tile"
      :class="[{ 'has-stream': hasStream }, streamState]"
      @click="playPauseStream"
    >
      <live-data
        :broadcast="tileBroadcast"
        :playing="streamState === 'playing'"
      />
      <div class="station">
        <img
          ref="logo"
          class="picture"
          :src="getImage(tileBroadcast.logoUrl) || tileBroadcast.logoUrl"
        >
        <div class="info">
          <div class="name">
            {{ tileBroadcast.name }}
          </div>
          <div v-if="tileBroadcast.band === 'DAB'" class="frequency">
            DAB
          </div>
          <div v-else class="frequency">
            {{ frequency }}
            <span class="frequency-separator">&middot;</span>
            {{ tileBroadcast.band }}
          </div>
        </div>
        <stream-indicator
          v-if="audio"
          :state="streamState"
          class="stream-indicator"
        />
      </div>
    </div>
  </transition>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { normalizedStreams } from '@ibiquity/dashboard.audio-player/src/utils';
import StreamIndicator from '@/components/common/StreamIndicator';
import LiveData from './_LiveData';

export default {
  name: 'Tile',
  components: {
    StreamIndicator,
    LiveData,
  },
  props: {
    tile: {
      type: Object,
      default: () => {},
    },
    index: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      audio: this.$route.query.audio,
    };
  },
  computed: {
    ...mapGetters('markets', ['broadcast']),
    ...mapGetters('playback', ['isGivenBroadcastPlaying', 'isGivenBroadcastCurrent', 'playerState']),
    ...mapGetters('prefetcher', { getImage: 'get' }),
    tileBroadcast() {
      return this.broadcast(this.tile.broadcastId);
    },
    frequency() {
      if (this.tileBroadcast.band === 'AM') return this.tileBroadcast.frequency;
      return ((this.tileBroadcast.frequency || 0) / 1000).toFixed(1);
    },
    playing() {
      return this.isGivenBroadcastPlaying(this.tile.broadcastId, this.index);
    },
    active() {
      return this.isGivenBroadcastCurrent({
        broadcastId: this.tile.broadcastId,
        index: this.index,
      });
    },
    normalizedStreams() {
      let streams = this.tileBroadcast.streams ? Array.from(this.tileBroadcast.streams) : [];
      if (this.tileBroadcast.streamUrl) streams.unshift({ url: this.tileBroadcast.streamUrl });
      streams = normalizedStreams(streams);
      return (streams.length ? streams : null);
    },
    streamState() {
      if (!this.hasStream) return 'no-stream';
      return this.active ? this.playerState : 'pause';
    },
    hasStream() {
      return this.audio && this.normalizedStreams;
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    'tile.broadcastId': function () {
      this.loadTile(this.tile.id);
    },
  },
  mounted() {
    this.loadTile(this.tile.id);
  },
  methods: {
    ...mapActions('markets', ['loadTile']),
    ...mapActions('playback', ['switchPlayback']),

    playPauseStream() {
      if (!this.audio) return;
      const { tile } = this.$refs;
      const isFadingOut = tile?.classList.contains('tile-leave-active');
      if (this.normalizedStreams && tile && !isFadingOut) {
        this.switchPlayback({
          broadcastId: this.tile.broadcastId,
          streams: this.normalizedStreams,
          index: this.index,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/variables";

$station-color: #fefefe;
$station-bakgroung-color: #363333;

%fill-parent {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.tile-enter-active, .tile-leave-active {
  transition: opacity 2s ease;
}

.tile-enter, .tile-leave-to {
  opacity: 0;
}

.tile {
  position: relative;
  height: 100%;
  width: 100%;
  box-shadow: 0 2px 40px 0 rgba(0, 0, 0, 0.3);

  background: #191919;

  &.has-stream {
    cursor: pointer;

    .stream-indicator {
      background-color: rgba(255, 225, 255, 0.08);
    }
  }

  &.playing {
    .station {
      background-color: $station-color;
      color: $station-bakgroung-color;
    }

    .stream-indicator {
      background-color: rgba(0, 0, 0, 0.05);
    }
  }

  .station {
    position: absolute;
    bottom: 0;
    background-color: $station-bakgroung-color;
    height: 25%;
    width: 100%;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    padding: 0.15em;
    overflow: hidden;
    transition: background-color 0.2s ease-in;

    .picture {
      height: 100%;
      border-radius: 5%;
      display: block;
      margin-right: 0.3em;
    }

    .info {
      overflow: hidden;
      width: 100%;
      padding-top: 0.1em;
    }

    .name {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      font-size: 0.51em;
      font-weight: $weight-bold;
      margin-bottom: 0.15em;
    }

    .frequency {
      font-weight: $weight-light;
      line-height: 1;
      font-size: 0.34em;
      letter-spacing: 0.4px;
      opacity: 0.5;

      .frequency-separator {
        padding: 0 0.2em;
      }
    }

    .stream-icon {
      width: 75%;
    }

    .streaming-icon {
      height: 75%;
      width: 75%;
      margin: 0 auto;
    }

    .spinner-icon,
    .error-icon {
      height: 95%;
      width: 95%;
      margin: 0 auto;
    }
  }

  .stream-indicator {
    margin-left: 0.25em;
  }
}
</style>
